import React, { useState, useEffect } from 'react';
import './FilterModal.css';
import axiosInstance from '../../Config/axiosConfig';

const FilterModal = ({ visible, onClose, onFilterSelect, currentFilters }) => {
  const [subjects, setSubjects] = useState([]);
  const [allEducationClasses, setAllEducationClasses] = useState([]);
  const [educationClasses, setEducationClasses] = useState([]);
  const [educationLevels, setEducationLevels] = useState([]);

  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [priceRange, setPriceRange] = useState(5000);

  const [openDropdown, setOpenDropdown] = useState(null);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  /* -------------------- Lookups -------------------- */
  useEffect(() => {
    if (!visible) return;

    const fetchLookups = async () => {
      try {
        const res = await axiosInstance.post('Public/get-lookups');
        if (res.data.code === 200) {
          setSubjects(res.data.data.subjects || []);
          setAllEducationClasses(res.data.data.educationClasses || []);

          // Remove Kindergarten (id=1) and College (id=5)
          const filteredLevels = (res.data.data.educationLevels || []).filter(
            level => level.id !== 1 && level.id !== 5
          );
          setEducationLevels(filteredLevels);
        }
        console.log("res lookup", res.data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchLookups();
  }, [visible]);

  /* -------------------- Restore Filters -------------------- */
  useEffect(() => {
    if (!visible) return;

    // Initialize with current filters
    setSelectedSubject(currentFilters?.subjectId || null);
    setSelectedClass(currentFilters?.educationClass || null);
    setSelectedLevel(currentFilters?.educationLevelId || null);
    setPriceRange(currentFilters?.maxPrice || 5000);
  }, [visible, currentFilters]);

  /* -------------------- Update Classes When Level Changes -------------------- */
  useEffect(() => {
    if (!selectedLevel) {
      setEducationClasses([]);
      return;
    }

    const filteredClasses = allEducationClasses.filter(
      cls => cls.education_level_id === selectedLevel
    );
    setEducationClasses(filteredClasses);
    // Only reset class if it's not compatible with the new level
    if (selectedClass && !filteredClasses.find(cls => cls.id === selectedClass)) {
      setSelectedClass(null);
    }
  }, [selectedLevel, allEducationClasses]);

  /* -------------------- Active State -------------------- */
  useEffect(() => {
    const hasFilters = selectedSubject || selectedClass || selectedLevel || priceRange < 10000;
    setHasActiveFilters(hasFilters);
  }, [selectedSubject, selectedClass, selectedLevel, priceRange]);

  /* -------------------- Apply -------------------- */
  const handleApply = () => {
    const filters = {};
    
    if (selectedSubject) filters.subjectId = selectedSubject;
    if (selectedClass) filters.educationClass = selectedClass;
    if (selectedLevel) filters.educationLevelId = selectedLevel;
    if (priceRange < 10000) filters.maxPrice = priceRange;
    
    onFilterSelect(filters);
  };

  const clearFilters = () => {
    setSelectedSubject(null);
    setSelectedClass(null);
    setSelectedLevel(null);
    setPriceRange(5000);
  };

  const getLabel = (type, list, value) =>
    value ? list.find(i => i.id === value)?.name : `Select ${type}`;

  if (!visible) return null;

  return (
    <>
      <div className="filter-modal-overlay" onClick={onClose}>
        <div className="filter-modal" onClick={e => e.stopPropagation()}>
          <div className="filter-modal-header">
            <h2>Filter Classes</h2>
            {hasActiveFilters && (
              <button className="clear-filters-button" onClick={clearFilters}>
                <span>🗑️</span>
                Clear All
              </button>
            )}
          </div>

          <div className="filter-modal-content">
            <div className="filter-section">
              <label>Education Level</label>
              <div className="dropdown-wrapper">
                <div
                  className={`dropdown-button ${selectedLevel ? 'selected' : ''} ${openDropdown === 'level' ? 'open' : ''}`}
                  onClick={() => setOpenDropdown(openDropdown === 'level' ? null : 'level')}
                >
                  <span>{getLabel('Level', educationLevels, selectedLevel)}</span>
                  <span className="dropdown-arrow">▼</span>
                </div>
                {openDropdown === 'level' && (
                  <div className="dropdown-list">
                    {educationLevels.map(item => (
                      <div
                        key={item.id}
                        className={`dropdown-item ${selectedLevel === item.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedLevel(item.id);
                          setOpenDropdown(null);
                        }}
                      >
                        {item.name}
                        {selectedLevel === item.id && <span className="check-mark">✓</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="filter-section">
              <label>Education Class</label>
              <div className="dropdown-wrapper">
                <div
                  className={`dropdown-button ${selectedClass ? 'selected' : ''} ${openDropdown === 'class' ? 'open' : ''}`}
                  onClick={() => setOpenDropdown(openDropdown === 'class' ? null : 'class')}
                  disabled={!selectedLevel}
                >
                  <span>{selectedLevel ? getLabel('Class', educationClasses, selectedClass) : 'Select Level First'}</span>
                  <span className="dropdown-arrow">▼</span>
                </div>
                {openDropdown === 'class' && selectedLevel && (
                  <div className="dropdown-list">
                    {educationClasses.map(item => (
                      <div
                        key={item.id}
                        className={`dropdown-item ${selectedClass === item.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedClass(item.id);
                          setOpenDropdown(null);
                        }}
                      >
                        {item.name}
                        {selectedClass === item.id && <span className="check-mark">✓</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="filter-section">
              <label>Subject</label>
              <div className="dropdown-wrapper">
                <div
                  className={`dropdown-button ${selectedSubject ? 'selected' : ''} ${openDropdown === 'subject' ? 'open' : ''}`}
                  onClick={() => setOpenDropdown(openDropdown === 'subject' ? null : 'subject')}
                >
                  <span>{getLabel('Subject', subjects, selectedSubject)}</span>
                  <span className="dropdown-arrow">▼</span>
                </div>
                {openDropdown === 'subject' && (
                  <div className="dropdown-list">
                    {subjects.map(item => (
                      <div
                        key={item.id}
                        className={`dropdown-item ${selectedSubject === item.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedSubject(item.id);
                          setOpenDropdown(null);
                        }}
                      >
                        {item.name}
                        {selectedSubject === item.id && <span className="check-mark">✓</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="filter-section">
              <label>Maximum Price</label>
              <div className="price-range-container">
                <input
                  type="range"
                  className="price-slider"
                  min="0"
                  max="10000"
                  step="500"
                  value={priceRange}
                  onChange={e => setPriceRange(+e.target.value)}
                />
                <div className="price-display">
                  <span>Budget Limit:</span>
                  <span className="price-value">Tsh {priceRange.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="filter-footer">
            <button className="apply-filter-button" onClick={handleApply}>
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterModal;